import {
  getFormConfig,
  type FormConfig,
  type SubmitQualificationInput,
} from '@nexofacil/schemas';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type GetBrokerResponse } from '@/lib/api';
import { applyBrokerTheme } from '@/lib/broker-theme';
import { readMetaCookies } from '@/lib/cookies';
import { clearFormDraft, loadFormDraft, saveFormDraft } from '@/lib/form-persistence';
import { initBrokerPixel, trackLeadWithDedup } from '@/lib/meta-pixel';
import { detectSubdomain } from '@/lib/subdomain';
import { getLeadSource } from '@/lib/source-store';
import { useDocumentTitle } from '@/lib/use-document-title';
import { SeoHead } from '@/components/seo-head';
import { LoadingState } from '@/components/ui/loading-state';
import { messages } from '@/messages';
import { getLandingContent } from '@nexofacil/schemas';
import { DynamicForm, type DynamicFormPayload } from '@/shared/form-engine';
import type { ResultPageState } from './qualification-result';

// =============================================================================
// Engine-driven qualification form. Resolves the right FormConfig from the
// broker's verticalKey (returned by /public/broker/:subdomain) · MCMV brokers
// see the MCMV form, PV brokers see the proteção-veicular form, etc.
//
// Wiring:
//   - Fetches the broker to learn its verticalKey (cached briefly across the
//     two renders this page goes through; the API call is cheap).
//   - Loads draft from sessionStorage (answers only, never contact).
//   - Persists draft on every change.
//   - Builds the wire payload (broker subdomain, source, client) and submits
//     through api.submitQualification. The backend resolves the broker's
//     vertical and dispatches to the right qualifier · same business rules
//     as before.
// =============================================================================

export function QualificationFormEnginePage() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const subdomain = useMemo(() => detectSubdomain() ?? '', []);
  const source = useMemo(() => getLeadSource(), []);
  const cookies = useMemo(() => readMetaCookies(), []);

  const [broker, setBroker] = useState<GetBrokerResponse['broker'] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const initialDraft = useMemo(() => loadFormDraft(), []);

  useDocumentTitle(broker ? `Qualificação · ${broker.name} · NexoFácil` : undefined);

  // Resolve broker first so we know which FormConfig to render.
  useEffect(() => {
    if (!subdomain) {
      setLoadError(messages.errorSubdomainMissing);
      return;
    }
    api
      .getBroker(subdomain)
      .then((res) => {
        const content = getLandingContent(res.broker.verticalKey);
        applyBrokerTheme(content.defaultTheme, res.broker.pageContent?.theme);
        initBrokerPixel(res.broker.metaPixelId);
        setBroker(res.broker);
      })
      .catch((err: unknown) => {
        setLoadError(err instanceof ApiError ? err.message : messages.errorGeneric);
      });
  }, [subdomain]);

  const config: FormConfig | null = broker ? getFormConfig(broker.verticalKey) : null;

  async function handleSubmit(payload: DynamicFormPayload) {
    setSubmitError(null);
    const apiPayload = buildApiPayload({
      payload,
      subdomain,
      slug,
      source,
      cookies,
    });
    try {
      const response = await api.submitQualification(apiPayload);
      trackLeadWithDedup(response.metaEventId);
      clearFormDraft();
      const state: ResultPageState = {
        ...response,
        broker: broker && {
          name: broker.name,
          avatarUrl: broker.avatarUrl,
          avatarShape: broker.pageContent?.avatarShape,
          verticalKey: broker.verticalKey,
        },
      };
      navigate('/resultado', { state, replace: true });
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : messages.errorGeneric);
    }
  }

  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
        <p className="max-w-md text-sm text-muted-foreground">{loadError}</p>
      </div>
    );
  }

  if (!config || !broker) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingState size="lg" />
      </div>
    );
  }

  return (
    <>
      <SeoHead noindex />
      <DynamicForm
        config={config}
      initialValues={{ answers: initialDraft?.answers as Record<string, unknown> | undefined }}
      initialStepIndex={
        initialDraft && initialDraft.step >= 1 && initialDraft.step <= config.steps.length
          ? initialDraft.step - 1
          : undefined
      }
      onChange={(state, stepIndex) => {
        // Draft shape is shared with the legacy MCMV form so users mid-flow
        // don't lose progress when the rotation flips.
        saveFormDraft({
          step: stepIndex + 1,
          answers: state.answers as Record<string, never>,
        });
      }}
      onSubmit={handleSubmit}
      onClose={() => navigate('/')}
      submitError={submitError}
      brand={{
        name: broker.name,
        logoUrl: broker.avatarUrl,
        avatarShape: broker.pageContent?.avatarShape,
      }}
      />
    </>
  );
}

// -----------------------------------------------------------------------------
// Wire payload builder. Coerces `null` (engine's "I don't have any" sentinel
// in money-bracket fields) to `undefined` so the persisted answers JSON
// matches what the legacy MCMV form already produces · keeps dashboards and
// reprocess scripts working on a single shape.
// -----------------------------------------------------------------------------
function buildApiPayload(args: {
  payload: DynamicFormPayload;
  subdomain: string;
  slug: string | undefined;
  source: SubmitQualificationInput['source'];
  cookies: { fbp?: string; fbc?: string };
}): SubmitQualificationInput {
  const { payload, subdomain, slug, source, cookies } = args;
  const lead = payload.lead as { name?: string; email?: string; phone?: string };
  return {
    brokerSubdomain: subdomain,
    propertySlug: slug,
    name: lead.name ?? '',
    email: lead.email ?? '',
    phone: lead.phone ?? '',
    answers: stripNulls(payload.answers),
    source,
    client: { fbp: cookies.fbp, fbc: cookies.fbc },
  };
}

function stripNulls(answers: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(answers)) {
    if (v === null) continue;
    out[k] = v;
  }
  return out;
}
